/**
 * useUsers Hook
 * Custom hook for user management data fetching and mutations
 * Uses React Query for caching, background updates, and optimistic updates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  getUserStats,
  getUserCredentials,
  getUserAdvanceTokenLedger,
  getUserUsageAnalytics,
  getUserMediaUsage,
  getUsers,
  revokeUserApiKey,
  updateUserEmbedWidgetStatus,
  createUser,
  updateUser,
  updateUserStatus,
  adjustUserTokens,
} from '../api/adminUsers.api';
import type {
  User,
  UserStatsData,
  UserUsageAnalytics,
  UserMediaUsage,
  AdvanceTokenLedgerHistory,
  AdminUserCredentials,
  GetUsersParams,
  GetUsersResponse,
  CreateUserInput,
  UpdateUserInput,
  UpdateUserStatusInput,
  AdjustUserTokenInput,
} from '../types';

// ============================================
// Query Keys
// ============================================

export const userQueryKeys = {
  all: ['admin', 'users'] as const,
  stats: () => [...userQueryKeys.all, 'stats'] as const,
  usage: (publicId?: string) => [...userQueryKeys.all, 'usage', publicId] as const,
  mediaUsage: (publicId?: string) => [...userQueryKeys.all, 'media-usage', publicId] as const,
  advanceTokenLedger: (
    publicId?: string,
    direction?: 'credit' | 'debit',
    page?: number,
    pageSize?: number
  ) => [...userQueryKeys.all, 'advance-token-ledger', publicId, direction, page, pageSize] as const,
  credentials: (publicId?: string) => [...userQueryKeys.all, 'credentials', publicId] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  list: (params?: GetUsersParams) => [...userQueryKeys.lists(), params] as const,
};

// ============================================
// User Stats Hook
// ============================================

/**
 * Fetch user statistics
 * Automatically refetches when user list changes
 */
export const useUserStats = (): UseQueryResult<UserStatsData, Error> => {
  return useQuery({
    queryKey: userQueryKeys.stats(),
    queryFn: getUserStats,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};

// ============================================
// User List Hook
// ============================================

/**
 * Fetch paginated user list with filters
 * Debounced search is handled at component level
 */
export const useUsers = (params?: GetUsersParams): UseQueryResult<GetUsersResponse, Error> => {
  return useQuery({
    queryKey: userQueryKeys.list(params),
    queryFn: () => getUsers(params),
    staleTime: 10000, // 10 seconds
  });
};

export const useUserUsageAnalytics = (
  publicId?: string
): UseQueryResult<UserUsageAnalytics, Error> => {
  return useQuery({
    queryKey: userQueryKeys.usage(publicId),
    queryFn: () => getUserUsageAnalytics(publicId as string),
    enabled: Boolean(publicId),
    staleTime: 30000,
  });
};

export const useUserMediaUsage = (publicId?: string): UseQueryResult<UserMediaUsage, Error> => {
  return useQuery({
    queryKey: userQueryKeys.mediaUsage(publicId),
    queryFn: () => getUserMediaUsage(publicId as string),
    enabled: Boolean(publicId),
    staleTime: 30000,
  });
};

export const useUserAdvanceTokenLedger = (
  publicId?: string,
  direction: 'credit' | 'debit' = 'credit',
  page = 1,
  pageSize = 20
): UseQueryResult<AdvanceTokenLedgerHistory, Error> => {
  return useQuery({
    queryKey: userQueryKeys.advanceTokenLedger(publicId, direction, page, pageSize),
    queryFn: () => getUserAdvanceTokenLedger(publicId as string, direction, page, pageSize),
    enabled: Boolean(publicId),
    staleTime: 30000,
  });
};

export const useUserCredentials = (
  publicId?: string
): UseQueryResult<AdminUserCredentials, Error> => {
  return useQuery({
    queryKey: userQueryKeys.credentials(publicId),
    queryFn: () => getUserCredentials(publicId as string),
    enabled: Boolean(publicId),
    staleTime: 10000,
  });
};

export const useRevokeUserApiKey = (): UseMutationResult<
  AdminUserCredentials,
  Error,
  { publicId: string; credentialPublicId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicId, credentialPublicId }) =>
      revokeUserApiKey(publicId, credentialPublicId),
    onSuccess: (data) => {
      queryClient.setQueryData(userQueryKeys.credentials(data.user.publicId), data);
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
  });
};

export const useUpdateUserEmbedWidgetStatus = (): UseMutationResult<
  AdminUserCredentials,
  Error,
  { publicId: string; widgetPublicId: string; status: 'active' | 'disabled' | 'revoked' }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicId, widgetPublicId, status }) =>
      updateUserEmbedWidgetStatus(publicId, widgetPublicId, status),
    onSuccess: (data) => {
      queryClient.setQueryData(userQueryKeys.credentials(data.user.publicId), data);
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
  });
};

// ============================================
// Create User Mutation
// ============================================

/**
 * Create new user mutation
 * Invalidates user list and stats on success
 */
export const useCreateUser = (): UseMutationResult<User, Error, CreateUserInput> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      // Invalidate and refetch user list and stats
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.stats() });
    },
  });
};

// ============================================
// Update User Mutation
// ============================================

/**
 * Update user (role, plan) mutation
 * Invalidates user list and stats on success
 */
export const useUpdateUser = (): UseMutationResult<
  User,
  Error,
  { publicId: string; data: UpdateUserInput }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicId, data }) => updateUser(publicId, data),
    onSuccess: () => {
      // Invalidate and refetch user list and stats
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.stats() });
    },
  });
};

// ============================================
// Update User Status Mutation
// ============================================

/**
 * Update user status (activate/suspend) mutation
 * Invalidates user list and stats on success
 */
export const useUpdateUserStatus = (): UseMutationResult<
  User,
  Error,
  { publicId: string; data: UpdateUserStatusInput }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicId, data }) => updateUserStatus(publicId, data),
    onSuccess: () => {
      // Invalidate and refetch user list and stats
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.stats() });
    },
  });
};

// ============================================
// Adjust User Advance Tokens Mutation
// ============================================

export const useAdjustUserTokens = (): UseMutationResult<
  User,
  Error,
  { publicId: string; data: AdjustUserTokenInput }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicId, data }) => adjustUserTokens(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.stats() });
    },
  });
};
